/**
 * 任务管理 Store (Pinia)
 *
 * 职责：
 * - 任务CRUD（主入口）
 * - 组装各个子模块（Filter, Batch, Download, SSE）
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { taskAPI } from '@/api/task'
import { taskAdapter } from '@/utils/task-adapter'
import { useEntitlementStore } from '@/store/entitlement'
import type { Task } from '@/types/task.domain'
import { TASK_STATUS } from '@/types/task.domain'
import type {
    CreateSingleTaskRequest,
    CreateBatchTaskRequest,
    TaskFilter,
    TaskSort,
    TaskType
} from '@/types/task.dto'
import type { TaskEvent } from '@/types/task.events'

// 子模块
import { taskSSEService } from './task-sse'
import { useTaskFilter } from './task-filter'
import { useTaskBatch } from './task-batch'
import { useTaskDownload } from './task-download'

export const useTaskStore = defineStore('task', () => {
    // ============= 状态定义 =============

    // 任务列表
    const tasks = ref<Task[]>([])
    const currentTask = ref<Task | null>(null)
    const isLoading = ref(false)
    const isCreating = ref(false)
    const error = ref<string | null>(null)

    // 分页
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0) // 总数

    // 选择状态
    const selectedTaskIds = ref<string[]>([])

    // 任务历史
    const taskHistory = ref<Task[]>([])

    // SSE 状态
    const isSubscribed = ref(false)
    const sseDebugEnabled = ref(import.meta.env.DEV)
    const sseConnectionStatus = ref<'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error'>('disconnected')
    const hasEverConnected = ref(false)

    // 初始化及定时器
    const isInitialized = ref(false)
    let unsubscribeTimer: any = null

    // 依赖 Store
    const entitlementStore = useEntitlementStore()

    // ============= 引入子模块 =============
    const {
        filter,
        sort,
        filteredTasks,
        setFilter,
        setSort
    } = useTaskFilter(tasks)

    const {
        downloadTaskResults,
        batchDownloadTasks
    } = useTaskDownload()

    // ============= 核心 Actions (Fetch/Create) =============

    /**
     * 获取任务列表
     */
    const fetchTasks = async (params?: {
        page?: number;
        pageSize?: number;
        filter?: TaskFilter;
        sort?: TaskSort;
    }) => {
        try {
            isLoading.value = true
            error.value = null

            // 同步更新内部 filter 状态，确保 filteredTasks 与 API 请求使用同一份 filter
            if (params?.filter) {
                setFilter(params.filter)
            }

            const queryParams = {
                page: params?.page || currentPage.value,
                pageSize: params?.pageSize || pageSize.value,
                filter: params?.filter || filter.value,
                sort: params?.sort || sort.value
            }

            const responseData = await taskAPI.getTasks(queryParams)

            tasks.value = taskAdapter.adaptTasks(responseData.tasks)
            total.value = responseData.total
            currentPage.value = responseData.page
            pageSize.value = responseData.pageSize

            // 标记为已初始化
            if (!isInitialized.value) {
                isInitialized.value = true
                manageSSESubscription()
            }
        } catch (err: any) {
            error.value = err.message || '获取任务列表失败'
            ElMessage.error(error.value || '获取任务列表失败')
        } finally {
            isLoading.value = false
        }
    }

    // 引入 Batch 模块 (需要 fetchTasks)
    const {
        deleteTasks,
        clearAllTasks,
        batchCancelTasks,
        batchRetryTasks,
        retryFailedTaskItems
    } = useTaskBatch(tasks, currentTask, total, async () => { await fetchTasks() })

    /**
     * 创建单次任务
     */
    const createSingleTask = async (request: CreateSingleTaskRequest): Promise<Task> => {
        try {
            isCreating.value = true
            error.value = null

            if (!isInitialized.value) isInitialized.value = true

            const rawTask = await taskAPI.createSingleTask(request)
            const task = taskAdapter.adaptTaskDTO(rawTask)

            tasks.value.unshift(task)
            currentTask.value = task

            ElMessage.success('任务创建成功')
            return task
        } catch (err: any) {
            error.value = err.message || '创建任务失败'
            ElMessage.error(error.value || '创建任务失败')
            throw err
        } finally {
            isCreating.value = false
        }
    }

    /**
     * 创建批量任务
     */
    const createBatchTask = async (request: CreateBatchTaskRequest): Promise<Task> => {
        try {
            isCreating.value = true
            error.value = null

            if (!isInitialized.value) isInitialized.value = true

            const rawTask = await taskAPI.createBatchTask(request)
            const task = taskAdapter.adaptTaskDTO(rawTask)

            tasks.value.unshift(task)
            currentTask.value = task

            ElMessage.success('批量任务创建成功')
            return task
        } catch (err: any) {
            error.value = err.message || '创建批量任务失败'
            ElMessage.error(error.value || '创建批量任务失败')
            throw err
        } finally {
            isCreating.value = false
        }
    }

    const createTask = async (request: CreateSingleTaskRequest | CreateBatchTaskRequest): Promise<Task> => {
        if (request.type === 'batch') {
            return createBatchTask(request as CreateBatchTaskRequest)
        } else {
            return createSingleTask(request as CreateSingleTaskRequest)
        }
    }

    // ============= 任务操作 (CRUD) =============

    const updateTask = async (taskId: string, updates: Partial<Task>) => {
        try {
            // 暂时只做本地更新
            const index = tasks.value.findIndex(t => t.taskId === taskId)

            if (index > -1) {
                tasks.value[index] = { ...tasks.value[index], ...updates } as Task
            }

            if (currentTask.value && currentTask.value.taskId === taskId) {
                currentTask.value = { ...currentTask.value, ...updates } as Task
            }

            return tasks.value[index]
        } catch (err: any) {
            ElMessage.error(err.message || '更新任务失败')
            throw err
        }
    }

    const deleteTask = async (taskId: string) => {
        try {
            await taskAPI.deleteTask(taskId)

            const index = tasks.value.findIndex(t => t.taskId === taskId)
            if (index > -1) {
                tasks.value.splice(index, 1)
            }

            if (currentTask.value && currentTask.value.taskId === taskId) {
                currentTask.value = null
            }

            ElMessage.success('任务删除成功')
        } catch (err: any) {
            ElMessage.error(err.message || '删除任务失败')
            throw err
        }
    }

    const refreshTaskStatus = async (taskId: string) => {
        try {
            const task = tasks.value.find(t => t.taskId === taskId)
            if (!task) throw new Error('任务不存在')
            await fetchTasks()
            ElMessage.success('任务状态已刷新')
        } catch (err: any) {
            ElMessage.error(err.message || '刷新任务状态失败')
            throw err
        }
    }

    const cancelTask = async (taskId: string) => {
        try {
            await updateTask(taskId, {
                status: 'cancelled',
                updatedAt: new Date().toISOString()
            })
            ElMessage.success('任务已取消')
        } catch (err: any) {
            ElMessage.error(err.message || '取消任务失败')
            throw err
        }
    }

    const retryTask = async (taskId: string) => {
        try {
            await updateTask(taskId, {
                status: 'created',
                progress: 0,
                error: undefined,
                updatedAt: new Date().toISOString()
            })
            ElMessage.success('任务已重新开始')
        } catch (err: any) {
            ElMessage.error(err.message || '重试任务失败')
            throw err
        }
    }

    const clearError = () => {
        error.value = null
    }

    const reset = () => {
        tasks.value = []
        currentTask.value = null
        taskHistory.value = []
        error.value = null
        setFilter({}) // Use setFilter to reset
        setSort({ field: 'createdAt', order: 'desc' }) // Use setSort to reset
        currentPage.value = 1
        total.value = 0
        selectedTaskIds.value = []
    }

    const loadTasks = async (forceRefresh = false) => {
        return await fetchTasks(forceRefresh ? { page: 1 } : undefined)
    }

    const loadUserLimits = async () => {
        // console.log('loadUserLimits called')
    }

    const stopStatusPolling = () => {
        // console.log('stopStatusPolling called')
    }

    /**
     * 注册外部创建的任务（如短剧配音批量任务）
     * 让 TaskStore 感知该任务，从而正确管理 SSE 生命周期和 hasActiveTasks 状态
     */
    const registerExternalTask = (taskId: string, options?: {
        type?: TaskType
        title?: string
        totalItems?: number
    }): Task => {
        // 避免重复注册
        const existing = tasks.value.find(t => t.taskId === taskId)
        if (existing) return existing

        if (!isInitialized.value) isInitialized.value = true

        const task: Task = {
            taskId,
            type: options?.type || 'batch',
            title: options?.title || '短剧配音任务',
            status: 'processing',
            priority: 'normal',
            progress: 0,
            userId: 0,
            createdAt: new Date().toISOString(),
            totalItems: options?.totalItems,
            completedItems: 0,
            failedItems: 0,
        }

        tasks.value.unshift(task)
        return task
    }

    /**
     * 确保 SSE 连接已建立（幂等）
     * 各场景在提交任务前统一调用此方法
     */
    const ensureSSEConnected = async (): Promise<void> => {
        if (!isSubscribed.value) {
            await subscribeGlobal()
        }
    }

    // ============= SSE 逻辑集成 =============

    const handleTaskEvent = (event: TaskEvent) => {
        const { taskId, type, data } = event
        const taskIndex = tasks.value.findIndex(t => t.taskId === taskId)

        if (taskIndex === -1) {
            // console.log(`[TaskStore] 收到未知任务事件`, { receivedTaskId: taskId })
            return
        }

        const task = tasks.value[taskIndex]

        switch (type) {
            case 'progress':
                if ('progress' in data) task.progress = data.progress
                if ('status' in data) task.status = data.status as any
                break
            case 'completed':
                task.status = 'completed'
                task.progress = 100
                if ('completedAt' in data) task.metadata = { ...task.metadata, completedAt: data.completedAt }
                if ('items' in data && Array.isArray(data.items)) {
                    if (!task.items) {
                        task.items = data.items as any
                    } else {
                        data.items.forEach((newItem: any) => {
                            const existingItem = task.items!.find(i => i.id === newItem.id)
                            if (existingItem) {
                                // Deep merge or specific field merge to prevent erasing properties like filename/durationSec
                                const oldSegment = existingItem.segment
                                const oldFile = existingItem.file
                                const oldDubbingLine = existingItem.dubbingLine

                                Object.assign(existingItem, newItem)

                                if (newItem.segment && oldSegment) {
                                    existingItem.segment = { ...oldSegment, ...newItem.segment }
                                }
                                if (newItem.file && oldFile) {
                                    existingItem.file = { ...oldFile, ...newItem.file }
                                }
                                if (newItem.dubbingLine && oldDubbingLine) {
                                    existingItem.dubbingLine = { ...oldDubbingLine, ...newItem.dubbingLine }
                                }
                            } else {
                                task.items!.push(newItem as any)
                            }
                        })
                    }
                }

                entitlementStore.refresh().catch(console.warn)
                break
            case 'failed':
            case 'processing_failed':
            case 'post_failed':
                task.status = type as any
                if ('error' in data) task.error = data.error as string
                if ('failurePhase' in data) task.failurePhase = data.failurePhase as any
                break
            case 'cancelled':
                task.status = 'cancelled'
                ElMessage.info(`任务 "${task.title}" 已取消`)
                break
            case 'item_progress':
                if ('itemId' in data) {
                    const item = task.items?.find(i => i.id === (data as any).itemId)
                    if (item && 'progress' in data) item.progress = data.progress
                }
                break
            case 'item_completed':
                if ('itemId' in data) {
                    const item = task.items?.find(i => i.id === (data as any).itemId)
                    if (item) {
                        item.status = 'completed'
                        item.progress = 100
                        const audioData = (data as any).audio
                        if (audioData) {
                            if (item.segment) {
                                item.segment.audioUrl = audioData.url
                                item.segment.durationSec = audioData.durationSec
                                item.segment.fileSize = audioData.fileSize
                                if (audioData.id) item.segment.primaryAssetId = audioData.id
                                if (audioData.filename) item.segment.filename = audioData.filename
                            } else if (item.file) {
                                item.file.audioUrl = audioData.url
                                item.file.durationSec = audioData.durationSec
                                item.file.fileSize = audioData.fileSize
                                if (audioData.id) item.file.primaryAssetId = audioData.id
                                if (audioData.filename) item.file.filename = audioData.filename
                            } else if (item.dubbingLine) {
                                item.dubbingLine.audioUrl = audioData.url
                                item.dubbingLine.durationSec = audioData.durationSec
                                item.dubbingLine.fileSize = audioData.fileSize
                                if (audioData.id) item.dubbingLine.primaryAssetId = audioData.id
                                if (audioData.filename) item.dubbingLine.filename = audioData.filename
                            }
                        }
                        if (task.type === 'batch' || task.type.startsWith('drama')) task.completedItems = (task.completedItems || 0) + 1
                    }
                }
                break
            case 'item_failed':
                if ('itemId' in data) {
                    const item = task.items?.find(i => i.id === (data as any).itemId)
                    if (item) {
                        item.status = 'failed'
                        const errorMsg = (data as any).error
                        if (errorMsg) item.errorMessage = errorMsg
                        if (task.type === 'batch' || task.type.startsWith('drama')) task.failedItems = (task.failedItems || 0) + 1
                    }
                }
                break
        }
        tasks.value[taskIndex] = { ...task }
    }

    // 监听 SSE 消息
    taskSSEService.onMessage(handleTaskEvent)

    // 监听连接状态
    taskSSEService.onStatusChange((status) => {
        sseConnectionStatus.value = status
        if (status === 'connected') {
            hasEverConnected.value = true
            isSubscribed.value = true
            manageSSESubscription()
        } else if (status === 'disconnected' || status === 'error') {
            isSubscribed.value = false
        }
    })

    const subscribeGlobal = async (): Promise<void> => {
        try {
            await taskSSEService.connect()
            // 真实订阅状态由 onStatusChange 监听器处理，但此处也可以再次同步
            isSubscribed.value = taskSSEService.isConnected()
        } catch (e) {
            console.error('[TaskStore] SSE 连接失败', e)
            isSubscribed.value = false
        }
    }

    const unsubscribeGlobal = () => {
        taskSSEService.disconnect()
        isSubscribed.value = false
    }

    const hasActiveTasks = computed(() => {
        if (!tasks.value.length) return false
        return tasks.value.some(task =>
            ['created', 'queued', 'processing', 'post_processing'].includes(task.status)
        )
    })

    // 智能管理SSE订阅
    const manageSSESubscription = () => {
        if (!isInitialized.value) return
        if (unsubscribeTimer) {
            clearTimeout(unsubscribeTimer)
            unsubscribeTimer = null
        }

        if (!hasActiveTasks.value && isSubscribed.value) {
            unsubscribeTimer = setTimeout(() => {
                if (!hasActiveTasks.value && isSubscribed.value) {
                    unsubscribeGlobal()
                    sseConnectionStatus.value = 'disconnected'
                    hasEverConnected.value = false
                }
            }, 5000)
        }
    }

    // 监听任务变化
    watch(hasActiveTasks, () => {
        manageSSESubscription()
    })

    // ============= 计算统计 =============

    const taskStats = computed(() => {
        const stats: any = {
            total: tasks.value.length,
            created: 0, queued: 0, processing: 0, post_processing: 0,
            completed: 0, failed: 0, cancelled: 0
        }
        tasks.value.forEach(task => {
            if (task.status && stats.hasOwnProperty(task.status)) {
                stats[task.status]++
            }
        })
        return stats
    })

    const runningTasks = computed(() => {
        return tasks.value.filter(task =>
            task.status === TASK_STATUS.QUEUED ||
            task.status === TASK_STATUS.PROCESSING ||
            task.status === TASK_STATUS.POST_PROCESSING
        )
    })

    return {
        // 状态
        tasks,
        currentTask,
        taskHistory,
        isLoading,
        isCreating,
        error,
        filter,
        sort,
        currentPage,
        pageSize,
        total,
        selectedTaskIds,
        isSubscribed,
        sseConnectionStatus,
        hasEverConnected,

        // 计算属性
        filteredTasks,
        taskStats,
        runningTasks,
        hasActiveTasks,

        // 基础方法
        createTask,
        fetchTasks,
        loadTasks,
        updateTask,
        deleteTask,
        cancelTask,
        retryTask,
        refreshTaskStatus,
        clearError,
        reset,
        loadUserLimits,
        stopStatusPolling,

        // 扩展方法 (Batch)
        deleteTasks, // 批量
        batchCancelTasks,
        batchRetryTasks,
        retryFailedTaskItems,
        clearAllTasks,

        // 扩展方法 (Download)
        downloadTaskResults,
        batchDownloadTasks,

        // 扩展方法 (Filter)
        setFilter,
        setSort,

        // SSE
        subscribeGlobal,
        unsubscribeGlobal,
        ensureSSEConnected,
        manageSSESubscription,
        registerExternalTask,
        getSSEMetrics: () => ({
            status: taskSSEService.getStatus(),
            connected: taskSSEService.isConnected()
        }),
        toggleSSEDebug: (enabled: boolean) => {
            sseDebugEnabled.value = enabled
        }
    }
})
